import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ChevronLeft, ChevronRight, BadgeCheck } from "lucide-react";
import { GOOGLE_REVIEWS, COMMUNITY_INTRO } from "@/data/content";
import { truncateAtSentence } from "@/lib/reviewText";

const LEAD_IN =
  "Our pilgrims consistently tell us the same thing: once the logistics were taken care of, they were free to focus on what mattered most — their worship.";

export default function FeaturedTestimonials({ compact = false, autoplay = true }) {
  const featured = GOOGLE_REVIEWS.filter((r) => r.featured);
  const [i, setI] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const total = featured.length;

  useEffect(() => {
    if (!autoplay || total < 2 || expanded) return;
    const id = setInterval(() => setI((p) => (p + 1) % total), 6500);
    return () => clearInterval(id);
  }, [autoplay, total, expanded]);

  // Reset the expanded state whenever the carousel advances to a new card.
  useEffect(() => {
    setExpanded(false);
  }, [i]);

  const r = featured[i];
  const { preview, isTruncated, endsAtSentence } = useMemo(
    () => (r ? truncateAtSentence(r.text) : { preview: "", isTruncated: false, endsAtSentence: true }),
    [r]
  );

  return (
    <section
      data-testid="featured-testimonials"
      className={`bg-white ${compact ? "py-16" : "py-24 md:py-28"}`}
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        {/* Introductory statement — full-page variant only */}
        {!compact && (
          <div
            data-testid="testimonials-lead-in"
            className="max-w-3xl mx-auto text-center mb-12 md:mb-16"
          >
            <span className="overline">Trusted by Our Community</span>
            <p className="mt-5 text-[var(--maqam-muted)] text-base md:text-lg leading-relaxed">
              {COMMUNITY_INTRO}
            </p>
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-10 md:mb-14">
          <div>
            <span className="overline">Trusted by Our Community</span>
            <h2 className="mt-3 font-serif text-3xl md:text-5xl text-[color:var(--maqam-navy)] leading-[1.05] tracking-tight">
              {compact ? "What our community says." : "In our pilgrims' own words."}
            </h2>
          </div>
          <div className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-[var(--maqam-muted)]">
            <BadgeCheck size={15} className="text-[color:var(--maqam-gold)]" />
            <span>Verified Community Reviews</span>
          </div>
        </div>

        {total === 0 ? null : (
          <div className="relative">
            <article
              key={i}
              data-testid="featured-testimonial-card"
              className="review-shadow rounded-3xl border border-[var(--maqam-border)] bg-white p-8 md:p-12 lg:p-14 fade-up min-h-[380px] md:min-h-[420px] flex flex-col"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center text-white font-serif text-xl"
                    style={{ background: "var(--maqam-navy)" }}
                  >
                    {r.initial}
                  </div>
                  <div>
                    <p className="font-semibold text-[color:var(--maqam-navy)] flex items-center gap-2">
                      {r.name}
                      <BadgeCheck size={16} className="text-[color:var(--maqam-gold)]" />
                    </p>
                    <p className="text-xs text-[var(--maqam-muted)] mt-0.5">
                      {[r.location, r.date].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} size={18} className="text-[color:var(--maqam-gold)]" style={{ fill: "currentColor" }} />
                  ))}
                </div>
              </div>

              <blockquote
                data-testid="featured-testimonial-quote"
                className="mt-8 font-serif text-xl md:text-2xl lg:text-[28px] leading-snug text-[color:var(--maqam-navy)] flex-1"
              >
                “{isTruncated && !expanded ? `${preview}${endsAtSentence ? "" : "…"}` : r.text}”
                {isTruncated && (
                  <button
                    type="button"
                    data-testid="featured-testimonial-toggle"
                    onClick={() => setExpanded((v) => !v)}
                    className="ml-2 align-baseline inline-flex items-center text-sm font-sans font-semibold text-[color:var(--maqam-navy)] underline underline-offset-4 decoration-[color:var(--maqam-gold)] decoration-2 hover:opacity-80 transition-opacity"
                    aria-expanded={expanded}
                  >
                    {expanded ? "Show Less" : "Read More"}
                  </button>
                )}
              </blockquote>

              <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[color:var(--maqam-gold-2)]">
                  <BadgeCheck size={14} className="text-[color:var(--maqam-gold)]" />
                  Verified Community Review
                </div>
                <span className="text-xs text-[var(--maqam-muted)] tracking-wide">
                  {i + 1} / {total}
                </span>
              </div>
            </article>

            <div className="mt-8 flex items-center justify-center gap-4">
              <button
                data-testid="featured-prev"
                onClick={() => setI((p) => (p - 1 + total) % total)}
                className="w-11 h-11 rounded-full border border-[var(--maqam-border)] hover:border-[color:var(--maqam-navy)] hover:bg-[color:var(--maqam-navy)] hover:text-white text-[color:var(--maqam-navy)] transition-colors inline-flex items-center justify-center"
                aria-label="Previous review"
              >
                <ChevronLeft size={18} />
              </button>

              <div className="flex items-center gap-2">
                {featured.map((_, idx) => (
                  <button
                    key={idx}
                    data-testid={`featured-dot-${idx}`}
                    onClick={() => setI(idx)}
                    className={`h-1.5 rounded-full transition-all ${idx === i ? "w-8" : "w-2.5"}`}
                    style={{ background: idx === i ? "var(--maqam-gold)" : "rgba(11,35,65,0.18)" }}
                    aria-label={`Show review ${idx + 1}`}
                  />
                ))}
              </div>

              <button
                data-testid="featured-next"
                onClick={() => setI((p) => (p + 1) % total)}
                className="w-11 h-11 rounded-full border border-[var(--maqam-border)] hover:border-[color:var(--maqam-navy)] hover:bg-[color:var(--maqam-navy)] hover:text-white text-[color:var(--maqam-navy)] transition-colors inline-flex items-center justify-center"
                aria-label="Next review"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="mt-10 text-center">
              <Link
                to="/testimonials"
                data-testid="featured-see-all-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--maqam-navy)] underline underline-offset-4 decoration-[color:var(--maqam-gold)] hover:decoration-2"
              >
                Read all community reviews →
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
